import { useState } from "react";
import { requestWalkthrough } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Check, Loader2 } from "lucide-react";

const AUDIENCES = [
  { v: "agency", label: "Small agency / NFP" },
  { v: "municipality", label: "Municipality" },
  { v: "funder", label: "Funder / foundation" },
  { v: "government", label: "Government" },
  { v: "other", label: "Other" },
];

export default function WalkthroughForm({ testid = "walkthrough-form" }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "", email: "", organization: "", role: "", audience: "", community: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.organization) return;
    setLoading(true);
    try {
      await requestWalkthrough({
        name: form.name,
        email: form.email,
        organization: form.organization,
        role: form.role || undefined,
        audience: form.audience || undefined,
        community: form.community || undefined,
        message: form.message || undefined,
      });
      setDone(true);
      toast({ title: "Request received.", description: "We'll be in touch within two business days." });
    } catch (err) {
      toast({ variant: "destructive", title: "Couldn't submit", description: err?.response?.data?.detail || "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid={`${testid}-success`} className="border border-[#FF8A00] bg-[#FF8A00]/5 p-6 flex items-start gap-4">
        <div className="w-10 h-10 bg-[#FF8A00] flex items-center justify-center flex-shrink-0">
          <Check className="text-[#050A15]" size={20} />
        </div>
        <div>
          <div className="font-bold text-[#0A1224] text-lg">Request received.</div>
          <p className="text-slate-600 mt-1">
            We'll respond within two business days from a real human inbox.
          </p>
        </div>
      </div>
    );
  }

  const inp = "w-full bg-white border border-slate-300 px-4 py-3 focus:border-[#0A1224] focus:outline-none transition-colors";
  const lbl = "block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2";

  return (
    <form data-testid={testid} onSubmit={onSubmit} className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className={lbl} htmlFor={`${testid}-name`}>Name <span className="text-[#FF8A00]">*</span></label>
          <input id={`${testid}-name`} data-testid={`${testid}-name-input`} required value={form.name} onChange={set("name")} className={inp} />
        </div>
        <div>
          <label className={lbl} htmlFor={`${testid}-email`}>Work email <span className="text-[#FF8A00]">*</span></label>
          <input id={`${testid}-email`} data-testid={`${testid}-email-input`} required type="email" value={form.email} onChange={set("email")} className={inp} />
        </div>
        <div>
          <label className={lbl} htmlFor={`${testid}-org`}>Organization <span className="text-[#FF8A00]">*</span></label>
          <input id={`${testid}-org`} data-testid={`${testid}-org-input`} required value={form.organization} onChange={set("organization")} className={inp} />
        </div>
        <div>
          <label className={lbl} htmlFor={`${testid}-role`}>Role / title</label>
          <input id={`${testid}-role`} data-testid={`${testid}-role-input`} value={form.role} onChange={set("role")} className={inp} />
        </div>
        <div>
          <label className={lbl} htmlFor={`${testid}-audience`}>I am a…</label>
          <select id={`${testid}-audience`} data-testid={`${testid}-audience-select`} value={form.audience} onChange={set("audience")} className={inp}>
            <option value="">Select…</option>
            {AUDIENCES.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
          </select>
        </div>
        <div>
          <label className={lbl} htmlFor={`${testid}-community`}>Community / region</label>
          <input id={`${testid}-community`} data-testid={`${testid}-community-input`} value={form.community} onChange={set("community")} className={inp} />
        </div>
      </div>
      <div>
        <label className={lbl} htmlFor={`${testid}-message`}>What's the question you'd most want answered?</label>
        <textarea id={`${testid}-message`} data-testid={`${testid}-message-input`} rows={4} value={form.message} onChange={set("message")} className={`${inp} resize-none`} />
      </div>
      <button type="submit" disabled={loading} data-testid={`${testid}-submit`} className="bg-[#FF8A00] text-[#050A15] font-bold px-6 py-3.5 hover:bg-[#FFA833] disabled:opacity-60 inline-flex items-center gap-2">
        {loading ? <Loader2 className="animate-spin" size={18} /> : null}
        Request a walkthrough
      </button>
      <p className="text-xs text-slate-500">
        We respond within two business days. No sales sequences, no demo bots — Chantal or an
        analyst replies.
      </p>
    </form>
  );
}
